import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { useDeleteFavoredContact } from '../useDeleteFavoredContact';
import { useGetFavoredContacts } from '../useGetFavoredContacts';
import { useStarAndUnstarFavoredContact } from '../useStarAndUnstarFavoredContact';
export const MAX_FAVORITES = 3;
export function useSelectContact(externalValue) {
    const { t } = useTranslation();
    const [contacts, setContacts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedContact, setSelectedContact] = useState();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [contactToDelete, setContactToDelete] = useState(null);
    const [showMaxFavoritesWarning, setShowMaxFavoritesWarning] = useState(undefined);
    const [showSuccessMessage, setShowSuccessMessage] = useState(undefined);
    const { loading: loadingDeleteContact, errorDeleteFavoredContactMessage, setErrorDeleteFavoredContactMessage, deleteFavoredContact, } = useDeleteFavoredContact();
    const { loading: loadingStarAndUnstar, errorStarFavoredContactMessage, setErrorStarFavoredContactMessage, errorUnstarFavoredContactMessage, setErrorUnstarFavoredContactMessage, starFavoredContact, unstarFavoredContact, } = useStarAndUnstarFavoredContact();
    const { loading: loadingContacts, errorGetFavoredContactsMessage, setErrorGetFavoredContactsMessage, errorGetFavoredBankContactsMessage, setErrorGetFavoredBankContactsMessage, favoredContacts, favoredBankContacts, getFavoredContacts, getFavoredBankContacts, } = useGetFavoredContacts();
    const loading = useMemo(() => {
        return loadingContacts || loadingStarAndUnstar || loadingDeleteContact;
    }, [loadingContacts, loadingStarAndUnstar, loadingDeleteContact]);
    const error = useMemo(() => {
        return (errorGetFavoredContactsMessage ||
            errorGetFavoredBankContactsMessage ||
            errorStarFavoredContactMessage ||
            errorUnstarFavoredContactMessage ||
            errorDeleteFavoredContactMessage);
    }, [
        errorGetFavoredContactsMessage,
        errorGetFavoredBankContactsMessage,
        errorStarFavoredContactMessage,
        errorUnstarFavoredContactMessage,
        errorDeleteFavoredContactMessage,
    ]);
    const filteredContacts = useMemo(() => {
        if (!searchQuery.trim()) {
            return contacts;
        }
        const query = searchQuery.toLowerCase();
        return contacts.filter(contact => contact.name.toLowerCase().includes(query) ||
            contact.details
                .find(detail => detail.type === 1)
                ?.data.pixKey?.toLowerCase()
                .includes(query));
    }, [contacts, searchQuery]);
    const favorites = useMemo(() => {
        return filteredContacts.filter(contact => contact.starred);
    }, [filteredContacts]);
    const regularContacts = useMemo(() => {
        return filteredContacts.filter(contact => !contact.starred);
    }, [filteredContacts]);
    useEffect(() => {
        if (externalValue === undefined || externalValue === '') {
            setSelectedContact(undefined);
        }
    }, [externalValue]);
    const initializeContacts = useCallback(() => {
        if (!favoredContacts &&
            !favoredBankContacts &&
            !errorGetFavoredContactsMessage &&
            !errorGetFavoredBankContactsMessage) {
            getFavoredContacts();
            getFavoredBankContacts();
        }
    }, [
        favoredContacts,
        favoredBankContacts,
        errorGetFavoredContactsMessage,
        errorGetFavoredBankContactsMessage,
        getFavoredContacts,
        getFavoredBankContacts,
    ]);
    useEffect(() => {
        initializeContacts();
    }, []);
    useEffect(() => {
        const allContacts = [...(favoredContacts ?? []), ...(favoredBankContacts ?? [])];
        setContacts(allContacts);
    }, [favoredContacts, favoredBankContacts]);
    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
    }, []);
    const handleContactSelect = useCallback((contact) => {
        setSelectedContact(contact);
    }, []);
    const handleContactSelectInternal = useCallback((contact, onChange) => {
        handleContactSelect(contact);
        onChange?.(contact);
    }, [handleContactSelect]);
    const handleSearchChange = useCallback((e) => {
        handleSearch(e.target.value);
    }, [handleSearch]);
    const handleStarContact = useCallback(async (contactId) => {
        if (favorites.length >= MAX_FAVORITES) {
            setShowMaxFavoritesWarning(t('select-contact-max-favorites-warning'));
            return;
        }
        const response = await starFavoredContact(contactId);
        if (response) {
            setContacts(prevContacts => prevContacts.map(c => (c.id === contactId ? { ...c, starred: true } : c)));
        }
    }, [starFavoredContact, favorites, t]);
    const handleUnstarContact = useCallback(async (contactId) => {
        const response = await unstarFavoredContact(contactId);
        if (response) {
            setContacts(prevContacts => prevContacts.map(c => (c.id === contactId ? { ...c, starred: false } : c)));
        }
    }, [unstarFavoredContact]);
    const handleDeleteContact = useCallback(async (contactId) => {
        const response = await deleteFavoredContact(contactId);
        if (response) {
            setShowSuccessMessage(t('select-contact-delete-modal-success-message'));
            getFavoredContacts();
            getFavoredBankContacts();
        }
    }, [deleteFavoredContact, setShowSuccessMessage, t, getFavoredContacts, getFavoredBankContacts]);
    const resetSelection = useCallback(() => {
        setSelectedContact(undefined);
    }, []);
    const resetSuccessMessage = useCallback(() => {
        setShowSuccessMessage(undefined);
    }, []);
    const resetErrorMessage = useCallback(() => {
        setErrorDeleteFavoredContactMessage(undefined);
        setErrorGetFavoredContactsMessage(undefined);
        setErrorGetFavoredBankContactsMessage(undefined);
        setErrorStarFavoredContactMessage(undefined);
        setErrorUnstarFavoredContactMessage(undefined);
    }, [
        setErrorDeleteFavoredContactMessage,
        setErrorGetFavoredContactsMessage,
        setErrorGetFavoredBankContactsMessage,
        setErrorStarFavoredContactMessage,
        setErrorUnstarFavoredContactMessage,
    ]);
    const resetErrorMaxFavoritesWarning = useCallback(() => {
        setShowMaxFavoritesWarning(undefined);
    }, []);
    const handleDeleteContactInternal = useCallback((contactId) => {
        setContactToDelete(contactId);
        setShowDeleteModal(true);
    }, []);
    const confirmDeleteContact = useCallback(() => {
        if (contactToDelete) {
            handleDeleteContact(contactToDelete);
        }
    }, [contactToDelete, handleDeleteContact]);
    const closeDeleteModal = useCallback(() => {
        setShowDeleteModal(false);
        setContactToDelete(null);
    }, []);
    return {
        contacts: filteredContacts,
        favorites,
        regularContacts,
        loading,
        error,
        searchQuery,
        selectedContact,
        showDeleteModal,
        contactToDelete,
        showMaxFavoritesWarning,
        showSuccessMessage,
        handleSearch,
        handleContactSelect,
        handleContactSelectInternal,
        handleSearchChange,
        handleStarContact,
        handleUnstarContact,
        resetSelection,
        resetSuccessMessage,
        resetErrorMessage,
        resetErrorMaxFavoritesWarning,
        handleDeleteContactInternal,
        confirmDeleteContact,
        closeDeleteModal,
        initializeContacts,
    };
}
